/**
 * Automatic Cart Restoration
 *
 * This script runs on every page load to restore the saved cart
 * after a "Buy Now" checkout session.
 *
 * Workflow:
 * 1. Check if we're NOT on checkout/thank-you pages
 * 2. Check if there's a saved cart in localStorage
 * 3. Restore the cart automatically
 * 4. Clean up localStorage
 *
 * Also handles browser back button via pageshow event
 */

class CartRestore {
  constructor() {
    this.STORAGE_KEY = 'iegeek_saved_cart';
    this.TIMESTAMP_KEY = 'iegeek_saved_cart_time';
    this.EXPIRY_HOURS = 72; // Cart restoration expires after 24 hours
    this.isRestoring = false; // Prevent duplicate restoration
    this.init();
  }

  async init() {
    // Initial restoration on page load
    await this.checkAndRestore();

    // Handle browser back button (bfcache)
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        // Page was loaded from cache (back/forward button)
        console.log('[Cart Restore] Page loaded from cache, checking for restoration...');
        this.checkAndRestore();
      }
    });
  }

  async checkAndRestore() {
    // Prevent duplicate restoration
    if (this.isRestoring) {
      console.log('[Cart Restore] Already restoring, skipping...');
      return;
    }

    // Don't restore on checkout or thank you pages
    if (this.isCheckoutOrThankYouPage()) {
      console.log('[Cart Restore] On checkout/thank-you page, skipping restoration');
      return;
    }

    // Check if there's a saved cart to restore
    const savedCart = this.getSavedCart();
    if (!savedCart || savedCart.length === 0) {
      return;
    }

    // Check if saved cart has expired
    if (this.isExpired()) {
      console.log('[Cart Restore] Saved cart expired (>24h), clearing');
      this.clearSavedCart();
      return;
    }

    // Restore the cart
    await this.restoreCart(savedCart);
  }

  /**
   * Get saved cart from localStorage
   */
  getSavedCart() {
    try {
      const savedCartJson = localStorage.getItem(this.STORAGE_KEY);
      if (!savedCartJson) return null;

      return JSON.parse(savedCartJson);
    } catch (error) {
      console.error('[Cart Restore] Failed to parse saved cart:', error);
      this.clearSavedCart();
      return null;
    }
  }

  /**
   * Check if saved cart has expired
   */
  isExpired() {
    try {
      const timestamp = localStorage.getItem(this.TIMESTAMP_KEY);
      if (!timestamp) return true;

      const savedTime = parseInt(timestamp);
      const now = Date.now();
      const hoursElapsed = (now - savedTime) / (1000 * 60 * 60);

      return hoursElapsed > this.EXPIRY_HOURS;
    } catch (error) {
      return true;
    }
  }

  /**
   * Check if we're on checkout or thank you page
   */
  isCheckoutOrThankYouPage() {
    const path = window.location.pathname;
    const search = window.location.search;

    return path.includes('/checkout') ||
           path.includes('/thank_you') ||
           path.includes('/orders/') ||
           search.includes('checkout_complete');
  }

  /**
   * Restore the saved cart
   */
  async restoreCart(savedItems) {
    this.isRestoring = true;

    try {
      console.log('[Cart Restore] Restoring cart with', savedItems.length, 'item(s)...');

      // Clear current cart first
      await fetch('/cart/clear.js', { method: 'POST' });

      // Re-add each saved item
      let restoredCount = 0;
      for (const item of savedItems) {
        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              id: item.variant_id,
              quantity: item.quantity,
              properties: item.properties || {}
            })
          });
          restoredCount++;
          console.log('[Cart Restore] ✓ Restored:', item.product_title);
        } catch (error) {
          console.error('[Cart Restore] Failed to restore item:', item.product_title, error);
        }
      }

      // Clear saved cart after successful restoration
      this.clearSavedCart();

      // Update cart UI
      this.notifyCartUpdated();

      console.log(`[Cart Restore] ✓ Restoration complete (${restoredCount}/${savedItems.length} items)`);

    } catch (error) {
      console.error('[Cart Restore] Failed to restore cart:', error);
      this.clearSavedCart();
    } finally {
      this.isRestoring = false;
    }
  }

  /**
   * Clear saved cart from localStorage
   */
  clearSavedCart() {
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.TIMESTAMP_KEY);
  }

  /**
   * Notify other scripts that cart has been updated
   */
  notifyCartUpdated() {
    // Dispatch events that theme scripts might be listening to
    try {
      document.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true
      }));

      if (window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent('cart:updated'));
      }

      // Refresh cart drawer if it exists
      if (typeof window.refreshCart === 'function') {
        window.refreshCart();
      }
    } catch (error) {
      console.warn('[Cart Restore] Failed to notify cart update:', error);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartRestore();
  });
} else {
  new CartRestore();
}
